<script>
import Stepper from './form-stepper'
import ProductStep from './steps/product-step'
import PriceStep from './steps/price-step'
import DimensionsStep from './steps/dimensions-step'
import ShippingStep from './steps/shipping-step'
import capitalize from 'lodash/capitalize'
import cloneDeep from 'lodash/cloneDeep'
import { mapActions } from 'vuex'
import FormMixin from '@lib/mixins/modal-form'
import get from 'lodash/get'

export default {
  name: 'ProductForm',
  components: {
    Stepper,
    ProductStep,
    PriceStep,
    DimensionsStep,
    ShippingStep,
  },
  mixins: [FormMixin],
  props: {
    providerId: {
      type: String,
      default: '',
    },
    providerProductId: {
      type: String,
      default: '',
    },

    supplierId: {
      type: String,
      default: '',
    },
    sku: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      required: true,
      validator: v => !v || /supplier|provider/.test(v),
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    backTo: {
      type: Object,
      default: () => {},
    },
    title: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      steps: ['product', 'price', 'dimensions', 'shipping'],
      loading: false,
      processing: false,
      form: {
        id: null,
        supplier_id: null,
        sku: null,
        name: null,
        description: null,
        brand: null,
        model: null,
        mpn: null,
        gtin: null,
        categories: null,
        images: [],
        pricing: {
          cost: null,
          price_before_tax: null,
          price_after_tax: null,
          rrp_before_tax: null,
          rrp_after_tax: null,
        },
        sales_tax_applicable: 'Free of Sales Tax',
        shipping: {
          shipping_before_tax: null,
          shipping_after_tax: null,
          dimensions: {
            unit: null,
            height: null,
            length: null,
            depth: null,
          },
          weight: {
            unit: null,
            value: null,
          },
        },
        currency: 'AUD',
        availability: {
          enabled: null,
          status: 'Available',
          start_date: null,
          end_date: null,
        },
        dimensions: {
          unit: null,
          height: null,
          length: null,
          depth: null,
        },
        weight: {
          unit: null,
          value: null,
        },
      },
      tree: {},
      salesTax: 0,
    }
  },
  computed: {
    current() {
      return this.$route.params.step || 'product'
    },
    stepTitle() {
      return `${capitalize(this.current)} Details`
    },
    componentName() {
      const { current } = this
      return `${capitalize(current)}Step`
    },
    productType() {
      const { type } = this

      return {
        get: `get${capitalize(type)}Product`,
        update: `update${capitalize(type)}Product`,
        create: 'createSupplierProduct',
        ownerId: `${type}Id`,
      }
    },
  },
  async created() {
    if (this.isEdit) {
      this.loading = true

      const {
        productType,
        type,
        providerId,
        providerProductId,
        supplierId,
        sku,
      } = this

      const getProduct = this[productType.get]


      if (type === 'provider') {
        const [, product] = await getProduct({ providerId, providerProductId })
        const copy = cloneDeep(product)
        this.form = {
          ...copy,
          shipping: {
            ...copy.shipping,
            ...this.formatPrices(copy, 'shipping'),
          },
          pricing: {
            ...this.formatPrices(copy, 'pricing'),
          },
        }
        const [, provider] = await this.getProvider(this.providerId)
        if (provider && provider.sales_tax_rate) this.salesTax = Number(provider.sales_tax_rate)
        if (provider && provider.category_tree_id) {
          const [, tree] = await this.getCategoryTree(provider.category_tree_id)
          this.tree = tree
        }
      } else {
        const [, product] = await getProduct({ supplierId, sku })
        const copy = cloneDeep(product)
        this.form = {
          ...copy,
          categories: product.categories ? product.categories.join(', ') : null,
          shipping: {
            ...copy.shipping,
            ...this.formatPrices(copy, 'shipping'),
          },
          pricing: {
            ...this.formatPrices(copy, 'pricing'),
          },
        }
      }

      this.$nextTick(() => {
        this.cloneOriginalData()
      })

      this.loading = false
    }
  },
  methods: {
    ...mapActions('providerProduct', [
      'getProviderProduct',
      'updateProviderProduct',
    ]),
    ...mapActions('supplierProduct', [
      'getSupplierProduct',
      'updateSupplierProduct',
      'createSupplierProduct',
    ]),
    ...mapActions('categoriesTree', ['getCategoryTree']),
    ...mapActions('providers', ['getProvider']),
    changeStep(next) {
      const { steps, current, backTo, $route, $router } = this
      const idx = steps.findIndex(item => item === current)
      const newIdx = next ? idx + 1 : idx - 1

      const newRoute = {
        name: $route.name,
        params: { ...$route.params, step: steps[newIdx] },
      }

      if (next) {
        this.validateForm().then(failed => {
          if (!failed) {
            $router.push(newRoute)
          }
        })
      } else {
        $router.push(newIdx >= 0 ? newRoute : { ...backTo, ...$route.params })
      }
    },
    updateValue({ field, value, index }) {
      if (field === 'images') {
        this.addImage(value, index)
      } else {
        if (field.includes('.')) {
          const nested = field.split('.')
          // TODO Refactor when possible
          switch (nested.length) {
            case 3:
              this.$set(this.form[nested[0]][nested[1]], nested[2], value)
              break
            default:
              this.$set(this.form[nested[0]], nested[1], value)
              break
          }
        } else {
          this.$set(this.form, field, value)
        }
      }
    },
    addImage(value, index = 0) {
      if (index === 'new') {
        this.form.images.push('')
      } else {
        this.form.images.splice(index, 1, value)
      }
    },
    validateForm() {
      const result = []
      this.$refs.form.$children[0].validate(valid => {
        result.push(valid)
      })
      return new Promise(resolve => {
        resolve(result.some(item => item === false))
      })
    },
    formatPrices(form, source) {
      const prices = {
        shipping: ['shipping_after_tax', 'shipping_before_tax'],
        pricing: ['cost', 'price_before_tax', 'price_after_tax', 'rrp_before_tax', 'rrp_after_tax'],
      }
      const formatted = {}

      for (const field of prices[source]) {
        const amount = get(form, source, {})[field]

        formatted[field] = typeof amount === 'number' ? amount.toFixed(2) : amount
      }

      return formatted
    },
    async onSubmit() {
      this.validateForm().then(async failed => {
        this.processing = true

        const { categories } = this.form

        if (!failed) {
          const requestType = this.isEdit ? this.productType.update : this.productType.create

          await this[requestType]({
            [this.productType.ownerId]: this[this.productType.ownerId],
            form: {
              ...this.form,
              categories: this.type === 'provider' ? categories : categories ? categories.split(', ') : [],
            },
            providerProductId: this.providerProductId,
          }).then(
            response => {
              this.processing = false
              this.$notifier({
                response,
                successMsg: `${capitalize(this.type)} Product successfully ${
                  this.isEdit ? 'updated' : 'created'
                }`,
              }).then(success => {
                if (success) {
                  this.$router.push({
                    name: `${this.type}-product`,
                    params: {
                      id: this.isEdit ? this.form.id : success.id,
                      sku: success.sku ? success.sku : null,
                      allow: true,
                    },
                  })
                }
              })
            }
          )
        }
      })
    },
  },
}
</script>

<template>
  <main-layout
    back
    :title="title"
    :loading="loading"
  >
    <el-card :class="$style.wrapper">
      <div slot="header">
        <stepper
          :steps="steps"
          :class="$style.stepper"
        />

        <span :class="$style.title">
          {{ stepTitle }}
        </span>
      </div>

      <component
        :is="componentName"
        ref="form"
        :class="$style.step"
        :form="form"
        :type="type"
        :tree="tree"
        :is-edit="isEdit"
        :sales-tax="salesTax"
        @change="updateValue"
      />
      <div :class="$style.controls">
        <el-button
          plain
          type="primary"
          @click="changeStep()"
        >
          Cancel
        </el-button>
        <el-button
          v-if="current === 'shipping'"
          type="primary"
          :loading="processing"
          @click="onSubmit"
        >
          Save
        </el-button>
        <el-button
          v-else
          type="primary"
          @click="changeStep('next')"
        >
          Next
        </el-button>
      </div>
    </el-card>
  </main-layout>
</template>

<style lang="scss" module>
.wrapper {
  :global {
    .el-card__header {
      padding-bottom: 0;
    }

    .el-card__body {
      padding-top: 0;
    }
  }
}

.stepper {
  margin-bottom: 2rem;
}

.step {
  width: 60rem;
  max-width: 100%;
  margin-top: 2rem;
}

.controls {
  display: flex;
  justify-content: space-between;
  width: 30rem;
  margin-top: 3rem;

  :global {
    .el-button {
      width: 47%;
    }
  }
}
</style>
